import React, { useState } from 'react';
import { supabase } from '../configs/supabase';
import { 
  Container,
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  IconButton,
  InputAdornment,
  Toolbar
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { validateEmail } from '../utils/validate';
import { colors } from '../utils/theme';
import Header from '../components/Header';
import { routingPath } from '../utils/path';
import { AuthenticationError, ValidationError } from '../utils/ErrorClass';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setEmailError('');
    setError('');
    try {
      if (!validateEmail(email)) throw new ValidationError('無効なメールアドレス形式です');
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw new AuthenticationError('メールアドレスとパスワードが一致しません');

      const { data: userCustomer, error: userCustomerError } = await supabase
        .from('user_customers')
        .select('customer_id')
        .eq('uid', data.user.id)
        .single();
      
      if (userCustomerError) throw new AuthenticationError('ユーザー情報の取得に失敗しました');

      const customerId = userCustomer.customer_id;
      const { data: customer, error: customerError } = await supabase
        .from('customer')
        .select('*')
        .eq('id', customerId)
        .single();

      if (customerError) throw new AuthenticationError('顧客情報の取得に失敗しました');
      if (customer.deleted_at !== null) throw new AuthenticationError('削除済みユーザーです。管理者に問い合わせください。');

      if (data) {
        navigate(routingPath.admin.orderList);
      } else {
        await supabase.auth.signOut();
      }
    } catch (err) {
      if (err instanceof ValidationError) {
        setEmailError(err.message);
      } else if (err instanceof AuthenticationError) {
        setError(err.message);
      } else { 
        setError('予期せぬエラーが発生しました。');
      }
      await supabase.auth.signOut();
    }

  };

  return (
    <>
      <Header />
      <Toolbar />
      <Container component="main" maxWidth="xs">
      <Box
        sx={{
        marginTop: 8,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: 'background.paper',
        padding: 3,
        borderRadius: 2,
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        backdropFilter: 'blur(10px)'
        }}
      >
        <Typography variant="h5" sx={{ mb: 2 }}>
          ログイン
        </Typography>
        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="email"
            name="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={!!emailError}
            helperText={emailError}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="password"
            type={showPassword ? 'text' : 'password'}
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            InputProps={{
            endAdornment: (
              <InputAdornment position="end">
              <IconButton
                  aria-label="toggle password visibility"
                  onClick={togglePasswordVisibility}
                  edge="end"
              >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
              </InputAdornment>
            ),
            }}
          />
          {error && <Alert severity="error" sx={{ width: '100%', mt: 2 }}>{error}</Alert>}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ 
            mt: 3,
            mb: 2,
            bgcolor: colors.mainColor,
            '&:hover': {
                bgcolor: colors.mainColor,
                '@media (hover: none)': {
                bgcolor: colors.mainColor,
                },
            },
            '&:active': {
                bgcolor: colors.mainColor,
            },
            '&:focus': {
                bgcolor: colors.mainColor,
            } 
            }}
          >
            ログイン
          </Button>
        </Box>
      </Box>
      </Container>
    </>
  );
}
